"use client";
import { ButtonProp } from "lib/gcms";
import { SectionTemplate } from "./section-template";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { customRichText } from "components/individuals/mdx-components";
import { Button, ButtonActions } from "@ui/components";
import { Gcms_ButtonTheme } from "lib/cms-typings";

export interface FeaturesStatSectionProps {
  title: string;
  bg: string;
  titleColor: string;
  /**
   * Background color of the stat block in the section
   */
  statBgColor: string;
  /**
   * Text color of the highlighted content in the stat block (`highlight.highlight`)
   */
  statHighlightColor: string;
  /**
   * Data for the highlight content
   */
  highlight: {
    /**
     * The highlighted stat in the stat block
     */
    highlight: string;
    /**
     * The rest of the text content in the stat block
     */
    description: string;
    /**
     * The source for the stat in the stat block
     */
    citation: string;
  };
  /**
   * Text content in the section
   */
  features: any;
  /**
   *  CTA button
   */
  button: ButtonProp;
}

export default function FeaturesStatSections(props: FeaturesStatSectionProps) {
  return (
    <SectionTemplate bg={props.bg}>
      <Heading
        as={"h2"}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        fontWeight="bold"
        lineHeight={1.2}
        mb={{
          base: 6,
          lg: 8,
        }}
      >
        {props.title}
      </Heading>
      <Flex
        flexDir={{
          base: "column",
          lg: "row",
        }}
        gap={{
          base: 8,
          lg: 16,
        }}
        align={{
          base: "center",
          lg: "unset",
        }}
      >
        {/* Stat Card */}
        <Flex
          borderRadius="2xl"
          bg={props.statBgColor}
          py="5"
          px="8"
          h={{
            base: "220px",
            lg: "330px",
          }}
          flexDir="column"
          justify="space-between"
          w={{
            base: "80%",
            lg: "30%",
          }}
        >
          <Box>
            <Text
              fontSize={{
                base: "3xl",
                md: "4xl",
                lg: "5xl",
              }}
              fontWeight="bold"
              color={props?.statHighlightColor}
            >
              {props?.highlight?.highlight}
            </Text>
            <Text
              fontSize={{
                base: "lg",
                lg: "2xl",
              }}
            >
              {props.highlight.description}
            </Text>
          </Box>
          <Text
            fontSize={{
              base: "xx-small",
              md: "xs",
            }}
          >
            {props.highlight.citation}
          </Text>
        </Flex>

        <Flex
          flexDir="column"
          align="center"
          w={{
            lg: "70%",
          }}
        >
          <Box>
            <RichText
              content={props.features.raw}
              renderers={customRichText("black", 1.5, 4)}
            />
          </Box>
          <Button
            __type={props.button.buttonAction as ButtonActions}
            href={props.button.href as string}
            colorScheme={props.button.theme as Gcms_ButtonTheme}
            variant={props.button.variant}
            label={props.button.label}
            mt="4"
            py={{
              base: "6",
              lg: "8",
            }}
            px="5"
          />
        </Flex>
      </Flex>
    </SectionTemplate>
  );
}
